import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiSave, FiX, FiFileText, FiUser, FiDollarSign, FiCalendar, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import Layout from '../components/Layout/Layout';
import api from '../services/api';

const Container = styled.div`
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  margin: 0;
`;

const Form = styled.form`
  background: ${props => props.theme?.colors?.surface || '#ffffff'};
  border-radius: 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  padding: 24px;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.cols || '1fr'};
  gap: 16px;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 8px;
  background: ${props => props.theme?.colors?.background || '#ffffff'};
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.primary || '#6366f1'};
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 8px;
  background: ${props => props.theme?.colors?.background || '#ffffff'};
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.primary || '#6366f1'};
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 8px;
  background: ${props => props.theme?.colors?.background || '#ffffff'};
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.primary || '#6366f1'};
  }
`;

const TemplateSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const TemplateOption = styled.div`
  padding: 16px;
  border: 2px solid ${props => props.selected ? props.theme?.colors?.primary || '#6366f1' : props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.selected ? (props.theme?.colors?.primary || '#6366f1') + '10' : 'transparent'};
  
  &:hover {
    border-color: ${props => props.theme?.colors?.primary || '#6366f1'};
  }
  
  .name {
    font-weight: 600;
    font-size: 14px;
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
    margin-bottom: 4px;
  }
  
  .category {
    font-size: 12px;
    color: ${props => props.theme?.colors?.textSecondary || '#888'};
    text-transform: capitalize;
  }
`;

const SearchSelect = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 8px;
  background: ${props => props.theme?.colors?.background || '#ffffff'};
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.primary || '#6366f1'};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: ${props => props.theme?.colors?.surface || '#ffffff'};
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  margin-top: 4px;
`;

const DropdownItem = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
  }
  
  .name {
    font-weight: 500;
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  }
  
  .email {
    font-size: 13px;
    color: ${props => props.theme?.colors?.textSecondary || '#888'};
  }
`;

const SelectedItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
  border-radius: 8px;
  margin-top: 8px;
  
  .info {
    .name {
      font-weight: 500;
      color: ${props => props.theme?.colors?.text || '#1a1a2e'};
    }
    .email {
      font-size: 13px;
      color: ${props => props.theme?.colors?.textSecondary || '#888'};
    }
  }
  
  button {
    background: none;
    border: none;
    color: ${props => props.theme?.colors?.textSecondary || '#888'};
    cursor: pointer;
    padding: 4px;
    
    &:hover {
      color: #dc2626;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &.primary {
    background: ${props => props.theme?.colors?.primary || '#6366f1'};
    color: white;
    
    &:hover {
      background: ${props => props.theme?.colors?.primaryDark || '#4f46e5'};
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
    
    &:hover {
      background: ${props => props.theme?.colors?.border || '#e5e7eb'};
    }
  }
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  
  &.error {
    background: #fee2e2;
    color: #dc2626;
  }
  
  &.success {
    background: #dcfce7;
    color: #16a34a;
  }
`;

export default function ContractForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  
  const [formData, setFormData] = useState({
    template_id: null,
    customer_id: null,
    order_id: null,
    title: '',
    amount: '',
    currency_code: 'USD',
    valid_from: '',
    valid_until: '',
    notes: '',
  });
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showOrderDropdown, setShowOrderDropdown] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch templates, customers, orders, currencies in parallel
      const [templatesRes, customersRes, currenciesRes] = await Promise.all([
        api.get('/contracts/templates?is_active=true'),
        api.get('/customers'),
        api.get('/currencies?is_active=true'),
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setCustomers(customersRes.data.customers || customersRes.data || []);
      setCurrencies(currenciesRes.data.currencies || []);
      
      // If editing, fetch the contract
      if (isEdit) {
        const contractRes = await api.get(`/contracts/${id}`);
        const contract = contractRes.data.contract;
        
        setFormData({
          template_id: contract.template_id,
          customer_id: contract.customer_id,
          order_id: contract.order_id,
          title: contract.title,
          amount: contract.amount || '',
          currency_code: contract.currency_code || 'USD',
          valid_from: contract.valid_from ? contract.valid_from.split('T')[0] : '',
          valid_until: contract.valid_until ? contract.valid_until.split('T')[0] : '',
          notes: contract.notes || '',
        });
        
        if (contract.customer) {
          setSelectedCustomer(contract.customer);
        }
        if (contract.order) {
          setSelectedOrder(contract.order);
        }
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch orders for selected customer
  useEffect(() => {
    const fetchOrders = async () => {
      if (selectedCustomer) {
        try {
          const res = await api.get(`/ecommerce/orders?customer_id=${selectedCustomer.id}`);
          setOrders(res.data.orders || []);
        } catch (err) {
          console.error('Failed to fetch orders:', err);
          setOrders([]);
        }
      } else {
        setOrders([]);
      }
    };
    fetchOrders();
  }, [selectedCustomer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTemplateSelect = (template) => {
    setFormData(prev => ({
      ...prev,
      template_id: template.id,
      title: prev.title || template.name,
    }));
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({ ...prev, customer_id: customer.id }));
    setCustomerSearch('');
    setShowCustomerDropdown(false);
    setSelectedOrder(null);
    setFormData(prev => ({ ...prev, order_id: null }));
  };

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    setFormData(prev => ({
      ...prev,
      order_id: order.id,
      amount: order.total || prev.amount,
      currency_code: order.currency_code || prev.currency_code,
    }));
    setOrderSearch('');
    setShowOrderDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.template_id) {
      setError('Please select a template');
      return;
    }
    
    if (!formData.title) {
      setError('Please enter a contract title');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      const payload = {
        template_id: formData.template_id,
        customer_id: formData.customer_id,
        order_id: formData.order_id,
        title: formData.title,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        currency_code: formData.currency_code,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
        notes: formData.notes,
      };
      
      if (isEdit) {
        await api.put(`/contracts/${id}`, payload);
      } else {
        await api.post('/contracts/generate', payload);
      }
      
      setSuccess('Contract saved successfully!');
      setTimeout(() => navigate('/contracts'), 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contract');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const search = customerSearch.toLowerCase();
    return (c.name && c.name.toLowerCase().includes(search)) ||
           (c.email && c.email.toLowerCase().includes(search)) ||
           (c.company_name && c.company_name.toLowerCase().includes(search));
  }).slice(0, 10);

  const filteredOrders = orders.filter(o => {
    const search = orderSearch.toLowerCase();
    return (o.order_number && o.order_number.toLowerCase().includes(search)) ||
           (o.id && o.id.toString().includes(search));
  }).slice(0, 10);

  if (loading) {
    return (
      <Layout>
        <Container>
          <p>Loading...</p>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Header>
          <Title>{isEdit ? 'Edit Contract' : 'Generate Contract'}</Title>
        </Header>

        {error && (
          <Alert className="error">
            <FiAlertCircle /> {error}
          </Alert>
        )}

        {success && (
          <Alert className="success">
            <FiCheckCircle /> {success}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <FormSection>
            <SectionTitle><FiFileText /> Select Template</SectionTitle>
            
            {templates.length === 0 ? (
              <Alert className="error">
                No active templates available. <a href="/contracts/templates/new">Create one first</a>.
              </Alert>
            ) : (
              <TemplateSelector>
                {templates.map(template => (
                  <TemplateOption
                    key={template.id}
                    selected={formData.template_id === template.id}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div className="name">{template.name}</div>
                    <div className="category">{template.category}</div>
                  </TemplateOption>
                ))}
              </TemplateSelector>
            )}
          </FormSection>

          <FormSection>
            <SectionTitle><FiUser /> Customer & Order</SectionTitle>
            
            <FormRow cols="1fr 1fr">
              <FormGroup>
                <Label>Customer</Label>
                <SearchSelect>
                  <SearchInput
                    placeholder="Search customers..."
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                  />
                  {showCustomerDropdown && customerSearch && filteredCustomers.length > 0 && (
                    <Dropdown>
                      {filteredCustomers.map(customer => (
                        <DropdownItem 
                          key={customer.id} 
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          <div className="name">{customer.name}</div>
                          <div className="email">{customer.email}</div>
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  )}
                </SearchSelect>
                {selectedCustomer && (
                  <SelectedItem>
                    <div className="info">
                      <div className="name">{selectedCustomer.name}</div>
                      <div className="email">{selectedCustomer.email}</div>
                    </div>
                    <button type="button" onClick={() => {
                      setSelectedCustomer(null);
                      setFormData(prev => ({ ...prev, customer_id: null }));
                    }}>
                      <FiX />
                    </button>
                  </SelectedItem>
                )}
              </FormGroup>
              
              <FormGroup>
                <Label>Order (optional)</Label>
                <SearchSelect>
                  <SearchInput
                    placeholder={selectedCustomer ? "Search orders..." : "Select customer first"}
                    value={orderSearch}
                    onChange={(e) => {
                      setOrderSearch(e.target.value);
                      setShowOrderDropdown(true);
                    }}
                    onFocus={() => setShowOrderDropdown(true)}
                    disabled={!selectedCustomer}
                  />
                  {showOrderDropdown && orderSearch && filteredOrders.length > 0 && (
                    <Dropdown>
                      {filteredOrders.map(order => (
                        <DropdownItem 
                          key={order.id} 
                          onClick={() => handleOrderSelect(order)}
                        >
                          <div className="name">Order #{order.order_number || order.id}</div>
                          <div className="email">{order.total ? `$${order.total}` : ''}</div>
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  )}
                </SearchSelect>
                {selectedOrder && (
                  <SelectedItem>
                    <div className="info">
                      <div className="name">Order #{selectedOrder.order_number || selectedOrder.id}</div>
                      <div className="email">{selectedOrder.total ? `$${selectedOrder.total}` : ''}</div>
                    </div>
                    <button type="button" onClick={() => {
                      setSelectedOrder(null);
                      setFormData(prev => ({ ...prev, order_id: null }));
                    }}>
                      <FiX />
                    </button>
                  </SelectedItem>
                )}
              </FormGroup>
            </FormRow>
          </FormSection>

          <FormSection>
            <SectionTitle><FiDollarSign /> Contract Details</SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label>Contract Title *</Label>
                <Input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Sales Agreement - Acme Corp"
                  required
                />
              </FormGroup>
            </FormRow>
            
            <FormRow cols="1fr 1fr">
              <FormGroup>
                <Label>Amount</Label>
                <Input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Currency</Label>
                <Select name="currency_code" value={formData.currency_code} onChange={handleInputChange}>
                  {currencies.length > 0 ? (
                    currencies.map(curr => (
                      <option key={curr.code} value={curr.code}>
                        {curr.code} - {curr.name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </>
                  )}
                </Select>
              </FormGroup>
            </FormRow>
          </FormSection>

          <FormSection>
            <SectionTitle><FiCalendar /> Validity Period</SectionTitle>
            
            <FormRow cols="1fr 1fr">
              <FormGroup>
                <Label>Valid From</Label>
                <Input
                  type="date"
                  name="valid_from"
                  value={formData.valid_from}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Valid Until</Label>
                <Input
                  type="date"
                  name="valid_until"
                  value={formData.valid_until}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </FormRow>
          </FormSection>

          <FormSection>
            <FormGroup>
              <Label>Notes</Label>
              <Textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Any additional notes about this contract..."
              />
            </FormGroup>
          </FormSection>

          <ButtonGroup>
            <Button type="button" className="secondary" onClick={() => navigate('/contracts')}>
              <FiX /> Cancel
            </Button>
            <Button type="submit" className="primary" disabled={saving}>
              <FiSave /> {saving ? 'Generating...' : (isEdit ? 'Save Changes' : 'Generate Contract')}
            </Button>
          </ButtonGroup>
        </Form>
      </Container>
    </Layout>
  );
}
